import { useCallback, useEffect, useId } from 'react'
import { useIntersectionObserver } from '#app/hooks/use-intersection-observer.ts'
import { useMainStore } from '#app/stores/main'

export function useChangeBackgroundColor<T extends Element>(
	backgroundColor: string,
) {
	const sectionId = useId() // Unique ID for this section
	const registerSection = useMainStore.use.registerSection()
	const unregisterSection = useMainStore.use.unregisterSection()

	const handleChange = useCallback(
		(isIntersecting: boolean, entry: IntersectionObserverEntry) => {
			if (isIntersecting) {
				// Register/update this section with its current intersection ratio
				registerSection(sectionId, backgroundColor, entry.intersectionRatio)
			} else {
				// Remove this section from tracking when it's not visible
				if (unregisterSection) unregisterSection(sectionId)
			}
		},
		[backgroundColor, registerSection, sectionId, unregisterSection],
	)

	const { ref } = useIntersectionObserver<T>({
		threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], // Multiple thresholds for better tracking
		rootMargin: '0px',
		onChange: handleChange,
	})

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			unregisterSection(sectionId)
		}
	}, [sectionId, unregisterSection])

	return ref
}
